(() => {
  const BLOG_PATH_PATTERN = /^\/blogs(?:\/|$)/i;
  const HOST_ASSET_PATTERN =
    /adsagent.*azurefd\.net\/(?:frontendInjection\.js|manifest\.json|assets\/(?:index|preview|chunk)[^"' )]*\.(?:css|js))/i;
  const LOWERED_Z_INDEX_ATTR = 'data-original-zindex';
  const FORCED_HIDDEN_ATTR = 'data-clarity-guard-hidden';
  const OBSERVED_ATTRS = ['style', 'class', 'aria-hidden', 'open'];
  const BODY_STYLE_PROPS = ['position', 'top', 'left', 'right', 'overflow', 'background-color'];
  const rootObservers = new WeakMap();
  let rafId = null;

  function queueScrub() {
    if (rafId !== null) return;

    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      watchClarityRoots();
      scrubClarityState();
    });
  }

  function shouldDisableBrandAgent() {
    return BLOG_PATH_PATTERN.test(window.location.pathname || '/');
  }

  function rootLooksLikeClarityAgent(root) {
    if (!root || !root.querySelectorAll) return false;

    const assetNodes = root.querySelectorAll('link[rel="stylesheet"][href], script[src]');
    const hasKnownAsset = Array.from(assetNodes).some((node) => {
      const assetUrl = node.href || node.src || '';
      return HOST_ASSET_PATTERN.test(assetUrl);
    });

    if (hasKnownAsset) return true;

    const knownUiSelectors = [
      '[data-testid="floating-view"]',
      '[data-testid="bottom-sheet-view"]',
      '[data-testid="image-carousel"]',
      '[role="dialog"][aria-modal="true"]',
    ];

    return knownUiSelectors.some((selector) => root.querySelector(selector));
  }

  function getClarityHosts() {
    if (!document.body) return [];

    const candidates = [document.body, ...document.body.querySelectorAll('*')];

    return candidates.filter((node) => {
      return node.shadowRoot && rootLooksLikeClarityAgent(node.shadowRoot);
    });
  }

  function getClarityRoots() {
    return getClarityHosts()
      .map((host) => host.shadowRoot)
      .filter(Boolean);
  }

  function removeClarityHosts() {
    getClarityHosts().forEach((host) => {
      host.remove();
    });
  }

  function removeClarityScripts() {
    document.querySelectorAll('script[src]').forEach((node) => {
      const src = node.src || '';
      if (HOST_ASSET_PATTERN.test(src)) {
        node.remove();
      }
    });
  }

  function isVisible(element) {
    if (!element || !element.isConnected) return false;

    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const opacity = Number.parseFloat(styles.opacity || '1');

    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      !Number.isNaN(opacity) &&
      opacity > 0.01 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function hasActiveDialog(root) {
    const selectors = [
      '[role="dialog"][aria-modal="true"]',
      '[data-testid="floating-view"]',
      '[data-testid="bottom-sheet-view"]',
    ];

    return selectors.some((selector) => {
      return Array.from(root.querySelectorAll(selector)).some(isVisible);
    });
  }

  function forEachRoot(callback) {
    const queue = [document];

    while (queue.length > 0) {
      const root = queue.shift();
      callback(root);

      if (!root.querySelectorAll) continue;

      root.querySelectorAll('*').forEach((node) => {
        if (node.shadowRoot) queue.push(node.shadowRoot);
      });
    }
  }

  function restoreLoweredZIndices() {
    forEachRoot((root) => {
      if (!root.querySelectorAll) return;

      root.querySelectorAll(`[${LOWERED_Z_INDEX_ATTR}]`).forEach((node) => {
        const original = node.getAttribute(LOWERED_Z_INDEX_ATTR);

        if (original) {
          node.style.zIndex = original;
        } else {
          node.style.removeProperty('z-index');
        }

        node.removeAttribute(LOWERED_Z_INDEX_ATTR);
      });
    });
  }

  function restoreBodyStyles() {
    if (!document.body) return;

    BODY_STYLE_PROPS.forEach((property) => {
      document.body.style.removeProperty(property);
    });
  }

  function resetForcedHiddenNodes(root) {
    root.querySelectorAll(`[${FORCED_HIDDEN_ATTR}]`).forEach((node) => {
      node.style.removeProperty('display');
      node.style.removeProperty('pointer-events');
      node.removeAttribute(FORCED_HIDDEN_ATTR);
    });
  }

  function looksLikeFullScreenBackdrop(node) {
    const styles = window.getComputedStyle(node);
    const rect = node.getBoundingClientRect();
    const bg = styles.backgroundColor || '';
    const isDarkBackdrop =
      bg === 'rgb(0, 0, 0)' ||
      bg.startsWith('rgba(0, 0, 0,') ||
      bg.startsWith('rgb(0 0 0');

    return (
      styles.position === 'fixed' &&
      rect.width >= window.innerWidth * 0.9 &&
      rect.height >= window.innerHeight * 0.9 &&
      isDarkBackdrop &&
      styles.pointerEvents !== 'none'
    );
  }

  function hideStaleLayers(root) {
    const staleSelectors = [
      '[role="dialog"][aria-modal="true"]',
      '[data-testid="floating-view"]',
      '[data-testid="bottom-sheet-view"]',
      '[data-testid="image-carousel"]',
    ];

    root.querySelectorAll('*').forEach((node) => {
      const isKnownLayer = staleSelectors.some((selector) => node.matches(selector));

      if (!isKnownLayer && !looksLikeFullScreenBackdrop(node)) return;

      node.style.setProperty('display', 'none', 'important');
      node.style.setProperty('pointer-events', 'none', 'important');
      node.setAttribute(FORCED_HIDDEN_ATTR, 'true');
    });
  }

  function scrubClarityState() {
    const roots = getClarityRoots();
    if (roots.length === 0) return;

    roots.forEach(resetForcedHiddenNodes);

    if (shouldDisableBrandAgent()) {
      restoreBodyStyles();
      restoreLoweredZIndices();
      removeClarityScripts();
      removeClarityHosts();
      return;
    }

    const hasOpenDialog = roots.some(hasActiveDialog);
    if (hasOpenDialog) return;

    restoreBodyStyles();
    restoreLoweredZIndices();
    roots.forEach(hideStaleLayers);
  }

  function watchClarityRoots() {
    getClarityRoots().forEach((root) => {
      if (rootObservers.has(root)) return;

      const observer = new MutationObserver(queueScrub);
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: OBSERVED_ATTRS,
      });

      rootObservers.set(root, observer);
    });
  }

  function startGuard() {
    if (!document.body) return;

    watchClarityRoots();
    scrubClarityState();

    const pageObserver = new MutationObserver(queueScrub);
    pageObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: OBSERVED_ATTRS,
    });

    window.addEventListener('pageshow', queueScrub);
    window.addEventListener('focus', queueScrub);
    window.setInterval(queueScrub, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startGuard, { once: true });
  } else {
    startGuard();
  }
})();
